import type { MapPoint } from './types';

/**
 * How far the pointer must travel before a press becomes a drag.
 *
 * Real pointer input has sub-pixel jitter, so without a floor every click on a marker would also
 * register as a one-frame drag and commit a "move" to the coordinate it was already at. 3px is the
 * same order as the platform's own drag threshold and is invisible to a deliberate gesture.
 */
export const DRAG_THRESHOLD_PX = 3;

/**
 * How long a press must be held **inside an Imported shape** before it may drag it (113609 user
 * story 32). Placement's own measurement, and Placement is the only caller that passes a hold.
 *
 * **Not the same constant as `DOUBLE_CLICK_WINDOW_MS` (250 ms, marking a Point in
 * `features/fields/use-field-points.ts`), and never to be reconciled with it** — different
 * gestures, from different criteria, in different stories, which happen to land on nearby numbers.
 * The same note `boundary-style.ts` carries about its three dash measurements: two numbers being
 * close is not evidence that they are one number.
 */
export const PLACEMENT_HOLD_MS = 200;

export type PointerDragOptions = {
  /** The live inverse projection — `MapProjection.toPoint`, read at the START of each move so a
   *  drag that runs across a camera change resolves against the camera it is actually on. Returns
   *  `null` while Google has no projection, which is a real state on first paint. */
  toPoint: (pixel: { x: number; y: number }) => MapPoint | null;
  /** The coordinate now under the pointer. Called on every move once the threshold is crossed —
   *  never before, so a plain click reports nothing. */
  onMove: (point: MapPoint) => void;
  /** The gesture is over. `dragged` is false for a press that never crossed the threshold and for
   *  a cancelled gesture, and `last` is the final reported coordinate or `null` when there was
   *  none — the two always agree, so a caller never has to check both. */
  onEnd: (dragged: boolean, last: MapPoint | null) => void;
  /** How long the press must be held, in ms, before a threshold crossing counts as a drag.
   *
   *  **Omit it — as every caller but Placement does — and there is no hold at all:** no timer is
   *  created and the gesture is exactly the distance-only one it has always been.
   *
   *  With one set, a crossing BEFORE the hold elapses does not start a drag and **voids the gesture
   *  permanently**: nothing is reported, `onEnd(false, null)` fires on release, and the caller is
   *  free to let the map pan. The hold cannot be earned late — a fast drag is a pan, not a slow
   *  pick-up. That disambiguates press-inside-and-drag-fast, which no AC covers (they cover
   *  press-*outside*); see grilling Q15. */
  holdMs?: number;
};

/**
 * Run one pointer-drag gesture: threshold detection, `window` listeners and their cleanup, and the
 * pixel → coordinate conversion.
 *
 * `window` rather than the element, because a drag routinely leaves the 21px marker it started on
 * and must keep tracking; `pointerup` there is also the only release a fast gesture reliably fires.
 *
 * **Client pixels are passed through as map-container pixels.** The map fills its container exactly
 * (`.map { width: 100%; height: 100% }` inside the page's positioned `.mapArea`), so the caller
 * subtracts the container's own origin before handing `toPoint` down — see the page. Nothing here
 * knows about the DOM beyond `window`, which is what keeps it a plain function.
 *
 * A plain function, not a hook: it owns no state that outlives the gesture, and each call's three
 * closures are added and removed by their own exact references, so two gestures can never confuse
 * each other's listeners.
 */
export function beginPointerDrag(
  start: { clientX: number; clientY: number },
  { toPoint, onMove, onEnd, holdMs }: PointerDragOptions,
): void {
  let dragged = false;
  let last: MapPoint | null = null;
  // No `holdMs` → held from the first frame, and no timer exists at all. That is what keeps the
  // distance-only gesture byte-for-byte what it was for every existing caller.
  let held = holdMs === undefined;
  // A crossing before the hold voids the gesture for good; `held` turning true afterwards cannot
  // revive it, which is why this is a second flag and not the absence of the first.
  let voided = false;
  const holdTimer =
    holdMs === undefined
      ? undefined
      : window.setTimeout(() => {
          held = true;
        }, holdMs);

  function handleMove(event: PointerEvent) {
    if (voided) return;
    if (!dragged) {
      const dx = event.clientX - start.clientX;
      const dy = event.clientY - start.clientY;
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      if (!held) {
        voided = true;
        return;
      }
      dragged = true;
    }
    const point = toPoint({ x: event.clientX, y: event.clientY });
    // A move the projection cannot answer is skipped rather than reported: there is no coordinate
    // to move to, and a stale one would silently teleport the Point.
    if (!point) return;
    last = point;
    onMove(point);
  }

  function cleanup() {
    window.clearTimeout(holdTimer);
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', end);
    window.removeEventListener('pointercancel', abort);
  }
  function end() {
    cleanup();
    // `last` gates `dragged`: crossing the threshold with no answerable coordinate is not a move.
    onEnd(last !== null, last);
  }
  function abort() {
    cleanup();
    onEnd(false, null);
  }

  window.addEventListener('pointermove', handleMove);
  window.addEventListener('pointerup', end);
  window.addEventListener('pointercancel', abort);
}
